// Annunci dell'app (area clienti): richieste, punti (ricariche in sede, trasferimenti, correzioni, incassi),
// annunci (controllo AI, togli/pubblica) e segnalazioni. I prezzi del servizio esistono solo qui, mai nell'app.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, Search, Plus, ArrowRightLeft, Pencil, Ban, CheckCircle2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/area-clienti-studio/annunci`;
const oggi = () => new Date().toISOString().slice(0, 10);
const dataOra = (s) => (s ? new Date(s).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '');
const errore = (e) => toast.error(e.response?.data?.detail || 'Errore');
const aggiornaBadge = () => window.dispatchEvent(new Event('jvservice:annunci-app'));
// id dell'operazione: se il pulsante parte due volte (doppio clic) il server la registra una volta sola
const nuovoId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const STATI = {
  in_verifica: 'In verifica', online: 'Online', bloccato: 'Bloccato dall\'AI', da_guardare: 'Da guardare', pausa: 'In pausa',
  pausa_punti: 'Pausa (punti finiti)', nascosto: 'Nascosto (segnalazioni)', tolto: 'Tolto', eliminato: 'Eliminato dal cliente',
};
const COLORE = { online: 'bg-emerald-100 text-emerald-800', da_guardare: 'bg-sky-100 text-sky-800', nascosto: 'bg-red-100 text-red-700',
  bloccato: 'bg-amber-100 text-amber-800', tolto: 'bg-slate-200 text-slate-700' };

function FotoStudio({ url }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let locale = '';
    axios.get(`${process.env.REACT_APP_BACKEND_URL}${url}`, { responseType: 'blob' })
      .then((r) => { locale = URL.createObjectURL(r.data); setSrc(locale); }).catch(() => {});
    return () => { if (locale) URL.revokeObjectURL(locale); };
  }, [url]);
  return src ? <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="" className="w-24 h-24 object-cover rounded" /></a> : <div className="w-24 h-24 bg-slate-100 rounded" />;
}

function CercaCliente({ onScegli, placeholder = 'Cerca cliente (nome, cognome, telefono)' }) {
  const [q, setQ] = useState('');
  const [elenco, setElenco] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setElenco([]); return undefined; }
    const t = setTimeout(() => axios.get(`${API}/clienti`, { params: { q } }).then((r) => setElenco(r.data.clienti)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <div className="flex items-center gap-2"><Search className="w-4 h-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} /></div>
      {elenco.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border rounded shadow max-h-64 overflow-y-auto">
          {elenco.map((c) => (
            <button key={c.id} type="button" onClick={() => { onScegli(c); setQ(''); setElenco([]); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
              <b>{c.nome}</b> · {c.telefono} · {c.punti} punti
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SchedaPunti({ cliente, onChiudi }) {
  const [d, setD] = useState(null);
  const [ric, setRic] = useState({ euro: '10', metodo: 'contanti', data_incasso: oggi(), nota: '' });
  const [tra, setTra] = useState({ a: null, punti: '', nota: '' });
  const [cor, setCor] = useState({ punti: '', motivo: '' });
  const [invio, setInvio] = useState('');
  const opRic = React.useRef(nuovoId());
  const opTra = React.useRef(nuovoId());
  const carica = useCallback(() => axios.get(`${API}/punti/${cliente.id}`).then((r) => setD(r.data)).catch(errore), [cliente.id]);
  useEffect(() => { carica(); }, [carica]);
  const ricarica = async () => {
    setInvio('ricarica');
    try {
      await axios.post(`${API}/punti/${cliente.id}/ricarica`, { ...ric, euro: Number(ric.euro), id_operazione: opRic.current });
      opRic.current = nuovoId(); toast.success('Ricarica registrata'); carica(); aggiornaBadge();
    } catch (e) { errore(e); }
    setInvio('');
  };
  const trasferisci = async () => {
    setInvio('trasferimento');
    try {
      await axios.post(`${API}/punti/trasferisci`, { da: cliente.id, a: tra.a.id, punti: Number(tra.punti), nota: tra.nota, id_operazione: opTra.current });
      opTra.current = nuovoId(); toast.success('Punti trasferiti'); setTra({ a: null, punti: '', nota: '' }); carica();
    } catch (e) { errore(e); }
    setInvio('');
  };
  const correggi = async () => {
    setInvio('correzione');
    try { await axios.post(`${API}/punti/${cliente.id}/correzione`, { punti: Number(cor.punti), motivo: cor.motivo }); toast.success('Correzione registrata'); setCor({ punti: '', motivo: '' }); carica(); } catch (e) { errore(e); }
    setInvio('');
  };
  if (!d) return <Loader2 className="w-5 h-5 animate-spin" />;
  return (
    <Card><CardContent className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex-1"><div className="font-semibold text-lg">{d.nome}</div><div className="text-sm text-slate-500">{d.telefono}</div></div>
        <div className="text-3xl font-bold text-emerald-700">{d.punti} <span className="text-base font-normal">punti</span></div>
        <Button variant="ghost" size="sm" onClick={onChiudi}><X className="w-4 h-4" /></Button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="space-y-2 border rounded p-3">
          <div className="font-medium flex items-center gap-1"><Plus className="w-4 h-4" />Ricarica (1 € = 1 punto, minimo 10 €)</div>
          <Input inputMode="numeric" value={ric.euro} onChange={(e) => setRic({ ...ric, euro: e.target.value.replace(/\D/g, '') })} placeholder="Euro" />
          <div className="flex gap-2">{['contanti', 'bonifico'].map((m) => <Button key={m} type="button" size="sm" variant={ric.metodo === m ? 'default' : 'outline'} onClick={() => setRic({ ...ric, metodo: m })}>{m}</Button>)}</div>
          <Input type="date" value={ric.data_incasso} onChange={(e) => setRic({ ...ric, data_incasso: e.target.value })} />
          <Input value={ric.nota} onChange={(e) => setRic({ ...ric, nota: e.target.value })} placeholder="Nota (facoltativa)" />
          <Button onClick={ricarica} disabled={Number(ric.euro) < 10 || !!invio}>Registra ricarica</Button>
          <p className="text-xs text-slate-500">Ricordarsi scontrino/fattura: il CRM non emette documenti fiscali.</p>
        </div>
        <div className="space-y-2 border rounded p-3">
          <div className="font-medium flex items-center gap-1"><ArrowRightLeft className="w-4 h-4" />Trasferisci ad altro cliente</div>
          {tra.a ? <div className="text-sm">A: <b>{tra.a.nome}</b> <Button variant="ghost" size="sm" onClick={() => setTra({ ...tra, a: null })}>cambia</Button></div>
            : <CercaCliente onScegli={(c) => setTra({ ...tra, a: c })} placeholder="Cliente che riceve" />}
          <Input inputMode="numeric" value={tra.punti} onChange={(e) => setTra({ ...tra, punti: e.target.value.replace(/\D/g, '') })} placeholder="Punti" />
          <Input value={tra.nota} onChange={(e) => setTra({ ...tra, nota: e.target.value })} placeholder="Nota (es. richiesta del cliente)" />
          <Button onClick={trasferisci} disabled={!tra.a || !Number(tra.punti) || !!invio}>Trasferisci</Button>
        </div>
        <div className="space-y-2 border rounded p-3">
          <div className="font-medium flex items-center gap-1"><Pencil className="w-4 h-4" />Correzione</div>
          <Input value={cor.punti} onChange={(e) => setCor({ ...cor, punti: e.target.value.replace(/[^\d-]/g, '') })} placeholder="Punti (es. -2 o 3)" />
          <Input value={cor.motivo} onChange={(e) => setCor({ ...cor, motivo: e.target.value })} placeholder="Motivo (obbligatorio)" />
          <Button variant="outline" onClick={correggi} disabled={!Number(cor.punti) || !cor.motivo.trim() || !!invio}>Registra correzione</Button>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50"><tr><th className="p-2 text-left">Quando</th><th className="p-2 text-left">Movimento</th><th className="p-2 text-right">Punti</th><th className="p-2 text-left">Dettagli</th></tr></thead>
        <tbody>{d.movimenti.map((m) => (
          <tr key={m.id} className="border-t">
            <td className="p-2">{dataOra(m.quando)}</td><td className="p-2">{m.tipo.replace('_', ' ')}</td>
            <td className={`p-2 text-right font-medium ${m.punti > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{m.punti > 0 ? `+${m.punti}` : m.punti}</td>
            <td className="p-2 text-slate-600">{[m.importo_eur && `${m.importo_eur} € ${m.metodo} del ${m.data_incasso}`, m.utente, m.nota].filter(Boolean).join(' · ')}</td>
          </tr>
        ))}</tbody>
      </table>
    </CardContent></Card>
  );
}

function SchedaAnnuncio({ a, onCambiato }) {
  const [motivo, setMotivo] = useState('');
  const togli = async () => { try { await axios.post(`${API}/elenco/${a.id}/togli`, { motivo }); toast.success('Annuncio tolto'); onCambiato(); } catch (e) { errore(e); } };
  const pubblica = async () => { try { const r = await axios.post(`${API}/elenco/${a.id}/pubblica`); toast.success(r.data.stato === 'online' ? 'Pubblicato' : `Stato: ${r.data.stato}`); onCambiato(); } catch (e) { errore(e); } };
  return (
    <Card><CardContent className="p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge className={COLORE[a.stato] || 'bg-slate-100 text-slate-700'}>{STATI[a.stato] || a.stato}</Badge>
        <span className="font-semibold">{a.titolo}</span>
        <span className="text-sm text-slate-500">{a.categoria} · {a.tipo} · {a.comune}{a.prezzo ? ` · € ${a.prezzo}` : ''}</span>
        <span className="text-sm ml-auto">{a.cliente.nome} · {a.cliente.telefono}</span>
      </div>
      <p className="text-sm whitespace-pre-line">{a.descrizione}</p>
      <div className="text-sm text-slate-600">Contatti: {Object.entries(a.contatti || {}).filter(([, v]) => v).map(([k, v]) => `${k} ${v}`).join(' · ')}</div>
      {a.foto.length > 0 && <div className="flex flex-wrap gap-2">{a.foto.map((u) => <FotoStudio key={u} url={u} />)}</div>}
      {a.esito_ai && <div className="text-sm"><b>Controllo AI:</b> {a.esito_ai.esito}{a.esito_ai.motivo ? ` – ${a.esito_ai.motivo}` : ''}</div>}
      {a.segnalazioni_aperte > 0 && <div className="text-sm text-red-700">Segnalazioni aperte: {a.segnalazioni_aperte}</div>}
      <div className="flex flex-wrap items-center gap-2">
        {['da_guardare', 'bloccato', 'nascosto'].includes(a.stato) && <Button size="sm" onClick={pubblica}><CheckCircle2 className="w-4 h-4 mr-1" />Pubblica</Button>}
        {!['tolto', 'eliminato'].includes(a.stato) && (
          <>
            <Input className="max-w-sm h-8" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo della rimozione (lo vede il cliente)" />
            <Button size="sm" variant="destructive" onClick={togli} disabled={!motivo.trim()}><Ban className="w-4 h-4 mr-1" />Togli</Button>
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t pt-2">
        <span className="text-xs text-slate-500">Inserzionista {a.cliente.nome}:</span>
        <Button size="sm" variant="outline" disabled={!motivo.trim()} title="Scrivi il motivo nel campo sopra"
          onClick={async () => { if (!window.confirm(`Bloccare ${a.cliente.nome}? Tutti i suoi annunci vengono tolti e non potrà più pubblicare.`)) return; try { await axios.post(`${API}/inserzionisti/${a.cliente.id}/blocca`, { motivo }); toast.success('Inserzionista bloccato'); onCambiato(); } catch (e) { errore(e); } }}>
          <Ban className="w-4 h-4 mr-1" />Blocca inserzionista
        </Button>
      </div>
      <details className="text-xs text-slate-500"><summary>Storico</summary>{(a.storico || []).map((s, i) => <div key={i}>{dataOra(s.quando)} · {s.azione} · {s.chi}{s.motivo ? ` · ${s.motivo}` : ''}</div>)}</details>
    </CardContent></Card>
  );
}

export default function AnnunciApp() {
  const [scheda, setScheda] = useState('richieste');
  const [riepilogo, setRiepilogo] = useState({});
  const [richieste, setRichieste] = useState(null);
  const [clientiPunti, setClientiPunti] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [filtroStato, setFiltroStato] = useState('da_guardare');
  const [annunci, setAnnunci] = useState(null);
  const [segnalazioni, setSegnalazioni] = useState([]);
  const [periodo, setPeriodo] = useState({ dal: `${oggi().slice(0, 8)}01`, al: oggi() });
  const [incassi, setIncassi] = useState(null);

  const [pubblicaDaApp, setPubblicaDaApp] = useState(null);
  const [bloccati, setBloccati] = useState([]);
  useEffect(() => { axios.get(`${API}/impostazioni`).then((r) => setPubblicaDaApp(r.data.pubblica_da_app)).catch(() => {}); }, []);
  const cambiaInterruttore = async () => {
    const nuovo = !pubblicaDaApp;
    if (!window.confirm(nuovo ? 'Riattivare la pubblicazione degli annunci dall\'app?' : 'Spegnere la pubblicazione dall\'app? I clienti potranno pubblicare solo dal sito clienti.crm-jv.com (utile durante le revisioni di Apple/Google).')) return;
    try { await axios.put(`${API}/impostazioni`, { pubblica_da_app: nuovo }); setPubblicaDaApp(nuovo); toast.success('Impostazione salvata'); } catch (e) { errore(e); }
  };
  const caricaBloccati = useCallback(() => axios.get(`${API}/inserzionisti/bloccati`).then((r) => setBloccati(r.data.bloccati)).catch(() => {}), []);
  const sblocca = async (b) => { await axios.post(`${API}/inserzionisti/${b.client_id}/sblocca`).catch(errore); caricaBloccati(); };
  const caricaRiepilogo = useCallback(() => axios.get(`${API}/riepilogo`).then((r) => setRiepilogo(r.data)).catch(() => {}), []);
  const caricaRichieste = useCallback(() => axios.get(`${API}/richieste`).then((r) => setRichieste(r.data.richieste)).catch(errore), []);
  const caricaPunti = useCallback(() => axios.get(`${API}/punti`).then((r) => setClientiPunti(r.data.clienti)).catch(errore), []);
  const caricaAnnunci = useCallback(() => { setAnnunci(null); axios.get(`${API}/elenco`, { params: { stato: filtroStato } }).then((r) => setAnnunci(r.data.annunci)).catch(errore); }, [filtroStato]);
  const caricaSegnalazioni = useCallback(() => axios.get(`${API}/segnalazioni`).then((r) => setSegnalazioni(r.data.segnalazioni)).catch(errore), []);
  const caricaIncassi = useCallback(() => axios.get(`${API}/incassi`, { params: periodo }).then((r) => setIncassi(r.data)).catch(errore), [periodo]);

  useEffect(() => { caricaRiepilogo(); }, [caricaRiepilogo, scheda]);
  useEffect(() => { if (scheda === 'richieste') caricaRichieste(); }, [scheda, caricaRichieste]);
  useEffect(() => { if (scheda === 'punti') { caricaPunti(); caricaIncassi(); } }, [scheda, caricaPunti, caricaIncassi]);
  useEffect(() => { if (scheda === 'annunci') caricaAnnunci(); }, [scheda, caricaAnnunci]);
  useEffect(() => { if (scheda === 'segnalazioni') caricaSegnalazioni(); }, [scheda, caricaSegnalazioni]);
  useEffect(() => { if (scheda === 'bloccati') caricaBloccati(); }, [scheda, caricaBloccati]);

  const archivia = async (r) => { await axios.post(`${API}/richieste/${r.id}/archivia`).catch(errore); caricaRichieste(); aggiornaBadge(); };
  const apriRicarica = async (r) => {
    const id = (r.client_ids || [])[0];
    if (!id) { toast.warning('Non è ancora in anagrafica: registralo come cliente, poi cercalo nella scheda Punti'); return; }
    const c = (await axios.get(`${API}/punti/${id}`)).data;
    setCliente({ id, nome: c.nome }); setScheda('punti');
  };
  const chiudiSegnalazione = async (s) => { await axios.post(`${API}/segnalazioni/${s.id}/chiudi`).catch(errore); caricaSegnalazioni(); caricaRiepilogo(); };

  const schede = [['richieste', `Richieste (${riepilogo.richieste || 0})`], ['punti', 'Punti e incassi'],
    ['annunci', `Annunci (${riepilogo.da_guardare || 0} da guardare)`], ['segnalazioni', `Segnalazioni (${riepilogo.segnalazioni || 0})`],
    ['bloccati', 'Inserzionisti bloccati']];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">Annunci app</h1>
        {pubblicaDaApp !== null && (
          <Button size="sm" variant={pubblicaDaApp ? 'outline' : 'destructive'} className="ml-auto" onClick={cambiaInterruttore}>
            Pubblicazione dall'app: {pubblicaDaApp ? 'ATTIVA' : 'SPENTA (solo dal sito)'}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">{schede.map(([k, t]) => <Button key={k} variant={scheda === k ? 'default' : 'outline'} onClick={() => setScheda(k)}>{t}</Button>)}</div>

      {scheda === 'richieste' && (
        <Card><CardContent className="p-4 space-y-2">
          <p className="text-sm text-slate-500">Clienti che hanno chiesto di pubblicare. Il prezzo si comunica su WhatsApp/WeChat; dopo il pagamento: Ricarica.</p>
          {richieste === null ? <Loader2 className="w-5 h-5 animate-spin" /> : !richieste.length ? <p className="text-sm">Nessuna richiesta aperta.</p> : richieste.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 border-t pt-2 text-sm">
              <span className="w-32 text-slate-500">{dataOra(r.quando)}</span><b>{r.nome || 'Non in anagrafica'}</b><span>{r.contatto}</span>
              <Button size="sm" className="ml-auto" onClick={() => apriRicarica(r)}>Ricarica</Button>
              <Button size="sm" variant="outline" onClick={() => archivia(r)}>Archivia</Button>
            </div>
          ))}
        </CardContent></Card>
      )}

      {scheda === 'punti' && (
        <>
          <Card><CardContent className="p-4 space-y-3">
            <CercaCliente onScegli={(c) => setCliente(c)} />
            {clientiPunti.length > 0 && (
              <div className="flex flex-wrap gap-2">{clientiPunti.map((c) => (
                <Button key={c.id} size="sm" variant="outline" onClick={() => setCliente(c)}>{c.nome} · {c.punti}</Button>
              ))}</div>
            )}
          </CardContent></Card>
          {cliente && <SchedaPunti key={cliente.id} cliente={cliente} onChiudi={() => { setCliente(null); caricaPunti(); }} />}
          <Card><CardContent className="p-4 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <b>Incassi ricariche</b>
              <Input type="date" className="w-40" value={periodo.dal} onChange={(e) => setPeriodo({ ...periodo, dal: e.target.value })} />
              <Input type="date" className="w-40" value={periodo.al} onChange={(e) => setPeriodo({ ...periodo, al: e.target.value })} />
            </div>
            {incassi && (
              <>
                <div className="text-sm">Contanti <b>€ {incassi.contanti}</b> · Bonifico <b>€ {incassi.bonifico}</b> · Totale <b>€ {incassi.totale}</b></div>
                <table className="w-full text-sm"><tbody>{incassi.ricariche.map((m) => (
                  <tr key={m.id} className="border-t"><td className="p-1">{m.data_incasso}</td><td className="p-1">{m.cliente}</td><td className="p-1">€ {m.importo_eur}</td><td className="p-1">{m.metodo}</td><td className="p-1 text-slate-500">{m.utente}</td></tr>
                ))}</tbody></table>
              </>
            )}
          </CardContent></Card>
        </>
      )}

      {scheda === 'annunci' && (
        <>
          <div className="flex flex-wrap gap-2">{['da_guardare', 'nascosto', 'bloccato', 'in_verifica', 'online', 'pausa_punti', 'tolto', ''].map((s) => (
            <Button key={s || 'tutti'} size="sm" variant={filtroStato === s ? 'default' : 'outline'} onClick={() => setFiltroStato(s)}>{s ? STATI[s] : 'Tutti'}</Button>
          ))}</div>
          {annunci === null ? <Loader2 className="w-5 h-5 animate-spin" /> : !annunci.length ? <p className="text-sm">Nessun annuncio.</p>
            : annunci.map((a) => <SchedaAnnuncio key={a.id} a={a} onCambiato={() => { caricaAnnunci(); caricaRiepilogo(); aggiornaBadge(); }} />)}
        </>
      )}

      {scheda === 'bloccati' && (
        <Card><CardContent className="p-4 space-y-2">
          {!bloccati.length ? <p className="text-sm">Nessun inserzionista bloccato.</p> : bloccati.map((b) => (
            <div key={b.client_id} className="flex flex-wrap items-center gap-3 border-t pt-2 text-sm">
              <b>{b.cliente.nome}</b><span>{b.cliente.telefono}</span><span className="text-slate-600">{b.motivo}</span>
              <span className="text-slate-500">{dataOra(b.quando)} · {b.da}</span>
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => sblocca(b)}>Sblocca</Button>
            </div>
          ))}
        </CardContent></Card>
      )}

      {scheda === 'segnalazioni' && (
        <Card><CardContent className="p-4 space-y-2">
          {!segnalazioni.length ? <p className="text-sm">Nessuna segnalazione aperta.</p> : segnalazioni.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-3 border-t pt-2 text-sm">
              <span className="w-32 text-slate-500">{dataOra(s.quando)}</span><b>{s.annuncio.titolo}</b><Badge>{s.motivo}</Badge><span className="text-slate-600">{s.testo}</span>
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => { setFiltroStato(''); setScheda('annunci'); }}>Vedi annunci</Button>
              <Button size="sm" variant="outline" onClick={() => chiudiSegnalazione(s)}>Chiudi</Button>
            </div>
          ))}
        </CardContent></Card>
      )}
    </div>
  );
}
